import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { IngredientCardV2 } from '@sg/garnish';
import { type IngredientModification } from '@sg/graphql-schema';

import { useDietaryRestrictions } from '@order/components';

import { checkIfBaseKind } from '../../../../state';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsIngredientCard = (
  props: ProductDetailsIngredientCardProps,
) => {
  const { ingredientModification, quantity, baseIngredientsCount, onPress } =
    props;

  const { formatMessage } = useIntl();
  const getRestrictionNotice = useDietaryRestrictions();

  // ─────────────────────────────────────────────────────────────────────

  const isBaseIngredient = checkIfBaseKind(ingredientModification);
  const formattedQuantity =
    isBaseIngredient && quantity > 0
      ? `${quantity}/${baseIngredientsCount}`
      : quantity;
  const shouldUseIconVariation = isBaseIngredient && baseIngredientsCount === 1;

  const dietaryRestrictionNote = getRestrictionNotice(
    ingredientModification.ingredient.dietaryProperties,
  );
  const accessibilityLabel = formatMessage(messages.a11yLabel, {
    quantity,
    ingredient_name: ingredientModification.ingredient.name,
  });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress(ingredientModification);
  }, [ingredientModification, onPress]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <IngredientCardV2.Pressable
      disabled={ingredientModification.outOfStock}
      accessibilityLabel={accessibilityLabel}
      onPress={handleOnPress}
    >
      <IngredientCardV2.ImageContainer>
        <IngredientCardV2.Image
          source={ingredientModification.ingredient.asset.url}
          accessibilityLabel={ingredientModification.ingredient.name}
        />

        {quantity && !ingredientModification.outOfStock ? (
          <IngredientCardV2.Quantity
            iconName="IconCheck"
            shouldUseIcon={shouldUseIconVariation}
          >
            {formattedQuantity}
          </IngredientCardV2.Quantity>
        ) : null}
      </IngredientCardV2.ImageContainer>

      <IngredientCardV2.Label>
        {ingredientModification.ingredient.name}
      </IngredientCardV2.Label>

      {ingredientModification.outOfStock ? (
        <IngredientCardV2.NoticeLabel>
          <FormattedMessage {...messages.unavailableLabel} />
        </IngredientCardV2.NoticeLabel>
      ) : null}

      {dietaryRestrictionNote && !ingredientModification.outOfStock ? (
        <IngredientCardV2.NoticeLabel>
          {dietaryRestrictionNote}
        </IngredientCardV2.NoticeLabel>
      ) : null}
    </IngredientCardV2.Pressable>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: `{quantity, select,
      0 {Add {ingredient_name}}
      other {Increase quantity of {ingredient_name} (current quantity: {quantity})}
    }`,
    description: 'Customization | Ingredient card | a11y label',
  },
  unavailableLabel: {
    defaultMessage: 'Unavailable',
    description: 'Customization | Ingredient card | Unavailable label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ProductDetailsIngredientCardProps = {
  ingredientModification: IngredientModification;
  quantity: number;
  baseIngredientsCount: number;
  onPress: (ingredientModification: IngredientModification) => void;
};
